import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Application } from '../types';
import CloseIcon from '@mui/icons-material/Close';

import { EmailSenderSettingsPanel } from './EmailSenderSettingsPanel';
import { EmailSenderLetterPreview } from './EmailSenderLetterPreview';

import { generateEmailHtml, EmailStudentInfo } from '../../../../utils/emailGenerator';
import { formatDateMsk } from '../../../../utils/date';

interface EmailSenderModalProps {
  isOpen: boolean;
  onClose: () => void;
  application: Application | null;
  onSuccess?: () => void;
}

export interface AdditionalEmailEntry {
  address: string;
  type: 'cc' | 'bcc';
}

export interface Recipient {
  id: string | number;
  name: string;
  email: string;
  type: 'to' | 'bcc';
  isActive: boolean;
  role: 'student' | 'specialist';
  additionalEmails: AdditionalEmailEntry[];
}

export interface StudentProgramData {
  studentId: string | number;
  programName: string;
  startDate: string;
  endDate: string;
  examDate: string;
}

export interface BulkProgramInput {
  startDate: string;
  endDate: string;
  examDate: string;
}

const DEFAULT_INTRO_TEXT = `<p>Добрый день!</p><p>Для обучения по программе пройдите по ссылке <a href="https://energy-sdo.ru">https://energy-sdo.ru</a> и авторизуйтесь по логину и паролю.</p>`;

const hasValidEmail = (email: string | null | undefined): boolean => {
  return !!(email && email.trim());
};

const CLOSE_DELAY_MS = 2000;

export const EmailSenderModal: React.FC<EmailSenderModalProps> = ({ isOpen, onClose, application, onSuccess }) => {

  const [introText, setIntroText] = useState(DEFAULT_INTRO_TEXT);
  const [specialists, setSpecialists] = useState<Recipient[]>([]);
  const [students, setStudents] = useState<Recipient[]>([]);

  const [excludedStudentIds, setExcludedStudentIds] = useState<number[]>([]);
  const [excludedSpecialistIds, setExcludedSpecialistIds] = useState<number[]>([]);

  const [programsData, setProgramsData] = useState<StudentProgramData[]>([]);
  const [bulkInputs, setBulkInputs] = useState<Record<string, BulkProgramInput>>({});
  const [uniquePrograms, setUniquePrograms] = useState<string[]>([]);

  const [expandedStudentIds, setExpandedStudentIds] = useState<Set<string | number>>(new Set());
  const [previewRecipientId, setPreviewRecipientId] = useState<string | number | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendSuccess, setSendSuccess] = useState<string | null>(null);
  const [isStatusVisible, setIsStatusVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const successCalledRef = useRef(false);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let fadeOutTimer: NodeJS.Timeout;
    let removeTimer: NodeJS.Timeout;

    if (sendSuccess) {
      setIsStatusVisible(true);
      fadeOutTimer = setTimeout(() => setIsStatusVisible(false), CLOSE_DELAY_MS);
      removeTimer = setTimeout(() => setSendSuccess(null), CLOSE_DELAY_MS + 300);
    }

    return () => {
      clearTimeout(fadeOutTimer);
      clearTimeout(removeTimer);
    };
  }, [sendSuccess]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      successCalledRef.current = false;
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setIntroText(DEFAULT_INTRO_TEXT);
    }
  }, [isOpen, application?.id]);

  useEffect(() => {
    if (application) {
      setExcludedStudentIds([]);
      setExcludedSpecialistIds([]);
      setIsClosing(false);

      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }

      const specialistId = application.specialistId ?? application.specialist;
      const specs: Recipient[] = application.specialist
        ? [{
            id: specialistId ?? 'spec-1',
            name: application.specialist,
            email: application.specialistEmail || '',
            type: 'to',
            isActive: hasValidEmail(application.specialistEmail),
            role: 'specialist',
            additionalEmails: []
          }]
        : [];
      setSpecialists(specs);

      const studs: Recipient[] = application.students.map(s => ({
        id: s.id,
        name: s.fullName,
        email: s.email || '',
        type: 'to',
        isActive: true,
        role: 'student',
        additionalEmails: []
      }));
      setStudents(studs);

      const firstWithEmail = [...specs, ...studs].find(r => hasValidEmail(r.email));
      if (firstWithEmail) {
        setPreviewRecipientId(firstWithEmail.id);
      } else if (specs.length > 0) {
        setPreviewRecipientId(specs[0].id);
      } else if (studs.length > 0) {
        setPreviewRecipientId(studs[0].id);
      }

      const realPrograms: StudentProgramData[] = application.students.flatMap((s: any) => {
         const progs = s.programs || [];
         return progs.map((p: any) => ({
             studentId: s.id,
             programName: p.name,
             startDate: formatDateMsk(p.startDate),
             endDate: formatDateMsk(p.endDate),
             examDate: formatDateMsk(p.examDate),
         }));
      });

      setProgramsData(realPrograms);

      const uniqueNames = Array.from(new Set(realPrograms.map(p => p.programName)));
      setUniquePrograms(uniqueNames);

      const initialBulk: Record<string, BulkProgramInput> = {};
      uniqueNames.forEach(name => {
        const firstOccurrence = realPrograms.find(p => p.programName === name);
        initialBulk[name] = {
            startDate: firstOccurrence?.startDate || '',
            endDate: firstOccurrence?.endDate || '',
            examDate: firstOccurrence?.examDate || ''
        };
      });
      setBulkInputs(initialBulk);

      if (application.students.length > 0) {
        setExpandedStudentIds(new Set([application.students[0].id]));
      }
    }
  }, [application]);

  const previewHtml = useMemo(() => {
    if (!application) return '';

    const currentRecipient = [...specialists, ...students].find(r => r.id === previewRecipientId);
    if (!currentRecipient) return '';

    let targetStudents: Recipient[] = [];

    if (currentRecipient.role === 'student') {
        const sData = students.find(s => s.id === currentRecipient.id);
        if (sData) targetStudents = [sData];
    } else {
        targetStudents = students.filter(s => s.role === 'student');
    }

    const studentsDataForGenerator: EmailStudentInfo[] = targetStudents.map(recip => {
        const original = application.students.find(orig => orig.id === recip.id);
        const myProgs = programsData.filter(p => p.studentId === recip.id);

        return {
            id: recip.id,
            fullName: recip.name,
            login: original?.login || '—',
            password: original?.password || '******',
            programs: myProgs.map(p => ({
                name: p.programName,
                term: (p.startDate || p.endDate)
                    ? `с ${p.startDate} по ${p.endDate} (экз. с ${p.examDate})`
                    : '—'
            }))
        };
    });

    return generateEmailHtml({
        recipientEmail: currentRecipient.email || 'Email не указан',
        recipientName: currentRecipient.name,
        recipientRole: currentRecipient.role,
        introText: introText,
        studentsData: studentsDataForGenerator
    });

  }, [application, introText, programsData, previewRecipientId, specialists, students]);

  const handleRemoveRecipient = (id: string | number, list: Recipient[], setList: React.Dispatch<React.SetStateAction<Recipient[]>>) => {
    const recipient = list.find(item => item.id === id);

    if (recipient) {
      const numericId = toNumericId(id);
      if (numericId !== null) {
        if (recipient.role === 'student') {
          setExcludedStudentIds(prev => [...prev, numericId]);
        } else {
          setExcludedSpecialistIds(prev => [...prev, numericId]);
        }
      }
    }

    setList(list.filter(item => item.id !== id));
    setProgramsData(prev => prev.filter(p => p.studentId !== id));
    if (previewRecipientId === id) setPreviewRecipientId(null);
  };

  const handleAddSubEmail = (
    recipientId: string | number,
    emailToAdd: string,
    list: Recipient[],
    setList: React.Dispatch<React.SetStateAction<Recipient[]>>
  ): boolean => {
      if (!emailToAdd) return false;

      setList(prev => prev.map(item => {
          if (item.id === recipientId) {
              const emailType: AdditionalEmailEntry['type'] = item.type === 'bcc' ? 'bcc' : 'cc';
              const newEntry: AdditionalEmailEntry = { address: emailToAdd, type: emailType };
              return {
                  ...item,
                  additionalEmails: [
                      ...item.additionalEmails,
                      newEntry
                  ]
              };
          }
          return item;
      }));

      return true;
  };

  const handleRemoveSubEmail = (
    recipientId: string | number,
    emailIndex: number,
    list: Recipient[],
    setList: React.Dispatch<React.SetStateAction<Recipient[]>>
  ) => {
      setList(prev => prev.map(item => {
          if (item.id === recipientId) {
              const newArr = [...item.additionalEmails];
              newArr.splice(emailIndex, 1);
              return { ...item, additionalEmails: newArr };
          }
          return item;
      }));
  };

  const handleToggleSubEmailType = (
    recipientId: string | number,
    emailIndex: number,
    list: Recipient[],
    setList: React.Dispatch<React.SetStateAction<Recipient[]>>
  ) => {
      setList(prev => prev.map(item => {
          if (item.id === recipientId) {
              const updated: AdditionalEmailEntry[] = item.additionalEmails.map((entry, idx) => {
                  if (idx !== emailIndex) return entry;
                  const nextType: AdditionalEmailEntry['type'] = entry.type === 'cc' ? 'bcc' : 'cc';
                  return {
                      ...entry,
                      type: nextType
                  };
              });
              return { ...item, additionalEmails: updated };
          }
          return item;
      }));
  };

  const toggleRecipientType = (id: string | number, list: Recipient[], setList: React.Dispatch<React.SetStateAction<Recipient[]>>) => {
    setList(list.map(item => {
      if (item.id === id) {
        if (item.type === 'to') return { ...item, type: 'bcc' };
        if (item.type === 'bcc') return { ...item, type: 'to' };
      }
      return item;
    }));
  };

  const toggleRecipientActive = (id: string | number, list: Recipient[], setList: React.Dispatch<React.SetStateAction<Recipient[]>>) => {
      setList(list.map(item => {
          if (item.id === id) {
            return { ...item, isActive: !item.isActive };
          }
          return item;
      }));
  };

  const handleDateChange = (
    studentId: string | number,
    programName: string,
    field: keyof StudentProgramData,
    value: string
  ) => {
    setProgramsData(prev => prev.map(p =>
      (String(p.studentId) === String(studentId) && p.programName === programName)
        ? { ...p, [field]: value }
        : p
    ));
  };

  const handleBulkInputChange = (programName: string, field: keyof BulkProgramInput, value: string) => {
      setBulkInputs(prev => ({
          ...prev,
          [programName]: { ...prev[programName], [field]: value }
      }));
  };

  const applyAllBulkSettings = () => {
      setProgramsData(prev => prev.map(p => {
          const settings = bulkInputs[p.programName];
          if (settings) {
             return {
                 ...p,
                 startDate: settings.startDate || p.startDate,
                 endDate: settings.endDate || p.endDate,
                 examDate: settings.examDate || p.examDate
             };
          }
          return p;
      }));
  };

  const toNumericId = (value: string | number): number | null => {
    if (typeof value === 'number') return value;
    const numeric = Number(value);
    return Number.isNaN(numeric) ? null : numeric;
  };

  const normalizeId = (value: string | number) => String(value);

  const splitEmailsByType = (recipient: Recipient) => {
    const cc = recipient.additionalEmails
      .filter(item => item.type === 'cc')
      .map(item => item.address);
    const bcc = recipient.additionalEmails
      .filter(item => item.type === 'bcc')
      .map(item => item.address);
    return { cc, bcc };
  };

  const buildSpecialistsData = (list: Recipient[]) => (
    list
      .filter(item => item.isActive)
      .map(item => {
        const parsedId = toNumericId(item.id);
        if (parsedId === null) return null;
        const { cc, bcc } = splitEmailsByType(item);
        return {
          specialist_id: parsedId,
          full_name: item.name,
          recipient: {
            email: item.email || '',
            cc,
            bcc
          }
        };
      })
      .filter(Boolean) as {
        specialist_id: number;
        full_name: string;
        recipient: { email: string; cc: string[]; bcc: string[] };
      }[]
  );

  const buildStudentPrograms = (studentId: string | number) => {
    const normalizedTarget = normalizeId(studentId);
    return programsData
      .filter(p => normalizeId(p.studentId) === normalizedTarget)
      .map(p => ({
        program_name: p.programName,
        training_start_date: p.startDate,
        training_end_date: p.endDate,
        exam_date: p.examDate
      }));
  };

  const buildStudentsData = (list: Recipient[]) => (
    list
      .map(item => {
        const parsedId = toNumericId(item.id);
        if (parsedId === null) return null;

        const isActive = item.isActive;
        const emailToSend = isActive ? (item.email || '') : '';
        const { cc, bcc } = isActive ? splitEmailsByType(item) : { cc: [], bcc: [] };

        return {
          student_id: parsedId,
          full_name: item.name,
          recipient: {
            email: emailToSend,
            cc,
            bcc
          },
          programs: buildStudentPrograms(item.id)
        };
      })
      .filter(Boolean) as {
        student_id: number;
        full_name: string;
        recipient: { email: string; cc: string[]; bcc: string[] };
        programs: ReturnType<typeof buildStudentPrograms>;
      }[]
  );

  const handleSendAll = async () => {
      if (!application) {
        setSendError('Заявка не выбрана.');
        setSendSuccess(null);
        setIsStatusVisible(true);
        return;
      }

      const token = localStorage.getItem('token');

      const studentsData = buildStudentsData(students);
      const specialistsData = buildSpecialistsData(specialists);

      if (!studentsData.length && !specialistsData.length) {
        setSendError('Не выбрано ни одного получателя или данных для отправки.');
        setSendSuccess(null);
        setIsStatusVisible(true);
        return;
      }

      setSendError(null);
      setSendSuccess(null);
      setIsStatusVisible(false);
      setIsClosing(false);

      const payload = {
          greeting: introText,
          students_data: studentsData,
          specialists_data: specialistsData,
          exclude_student_ids: excludedStudentIds,
          exclude_specialist_ids: excludedSpecialistIds
      };

      console.log('Sending payload:', JSON.stringify(payload, null, 2));

      try {
          setIsSending(true);

          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/applications/${application.id}/send-emails`, {
              method: 'POST',
              headers: {
                  'Accept': 'application/json',
                  'Content-Type': 'application/json',
                  'Authorization': token || ''
              },
              body: JSON.stringify(payload)
          });

          if (!response.ok) {
              const errorText = await response.text().catch(() => '');
              throw new Error(errorText || `Ошибка отправки: ${response.status}`);
          }

          try {
              const statusResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/v1/applications/${application.id}/status`, {
                  method: 'PATCH',
                  headers: {
                      'Accept': 'application/json',
                      'Content-Type': 'application/json',
                      'Authorization': token || ''
                  },
                  body: JSON.stringify({ status: 'sent' })
              });

              if (!statusResponse.ok) {
                  console.warn('Не удалось обновить статус заявки автоматически');
              }
          } catch (statusError) {
              console.error('Ошибка при обновлении статуса:', statusError);
          }

          setSendError(null);
          setSendSuccess('Письма успешно отправлены!');
          setIsStatusVisible(true);

          setIsClosing(true);

          closeTimerRef.current = setTimeout(() => {
            onClose();

            if (onSuccess && !successCalledRef.current) {
              successCalledRef.current = true;
              setTimeout(() => {
                onSuccess();
              }, 100);
            }
          }, CLOSE_DELAY_MS);

      } catch (error) {
          console.error('Ошибка отправки писем:', error);
          setSendSuccess(null);
          setSendError('Не удалось отправить письма. Проверьте консоль для деталей.');
          setIsStatusVisible(true);
      } finally {
          setIsSending(false);
      }
  };

  const handleManualClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    onClose();
  };

  if (!isOpen || !application) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-100">
      <div className="h-14 bg-blue-600 text-white flex items-center justify-between px-6 shadow-md shrink-0 z-30">
        <h2 className="text-lg font-semibold flex items-center gap-2">
           Расширенная отправка писем
        </h2>
        <button
          onClick={handleManualClose}
          disabled={isClosing}
          className={`p-1 rounded-full transition outline-none ${
            isClosing ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'
          }`}
        >
          <CloseIcon />
        </button>
      </div>

      <div className="flex-1 grid grid-cols-12 h-[calc(100vh-3.5rem)] overflow-hidden bg-gray-100">

        <EmailSenderSettingsPanel
            introText={introText}
            setIntroText={setIntroText}
            specialists={specialists}
            students={students}
            setSpecialists={setSpecialists}
            setStudents={setStudents}
            onRemoveRecipient={handleRemoveRecipient}
            onToggleType={toggleRecipientType}
            onToggleActive={toggleRecipientActive}
            onAddSubEmail={handleAddSubEmail}
            onRemoveSubEmail={handleRemoveSubEmail}
            onToggleSubEmailType={handleToggleSubEmailType}
            programsData={programsData}
            onDateChange={handleDateChange}
            bulkInputs={bulkInputs}
            uniquePrograms={uniquePrograms}
            onBulkInputChange={handleBulkInputChange}
            onApplyBulk={applyAllBulkSettings}
            expandedStudentIds={expandedStudentIds}
            setExpandedStudentIds={setExpandedStudentIds}
            previewRecipientId={previewRecipientId}
            setPreviewRecipientId={setPreviewRecipientId}
        />

        <EmailSenderLetterPreview
            previewHtml={previewHtml}
            onClose={handleManualClose}
            onSend={handleSendAll}
            isSending={isSending}
            sendError={sendError}
            sendSuccess={sendSuccess}
            isStatusVisible={isStatusVisible}
            isClosing={isClosing}
            closeDelayMs={CLOSE_DELAY_MS}
        />

      </div>
    </div>
  );
};